import React, { useEffect, useMemo, useState } from "react";
import { FaImage, FaPen, FaSortDown, FaSortUp } from "react-icons/fa";

import { contractAddIcon } from "../../../assetData/icons";
import Table from "../../../components/backoffice/TemplateBroker/Table";
import IndeterminateCheckbox from "../../../components/ui/IndeterminateCheckbox";
import { sortData } from "../../../utils/sortData";
import { RiImageEditFill } from "react-icons/ri";

const TemplatePMSConfig = ({
  rows,
  templates,
  schemes,
  loading,
  syncing,
  handleSyncData,
  setPopup,
}) => {
  const tableData = useMemo(() => rows.map(({ mapping, template, scheme }) => ({
    ...mapping,
    mapping,
    template,
    scheme,
    mapping_id: mapping?.id ?? mapping?.mapping_id,
    template_id: template?.id ?? template?.template_id,
    template_name: template?.name ?? template?.template_name,
    scheme_name: scheme?.name ?? scheme?.scheme_name ?? scheme?.label,
    file_format: template?.format ?? template?.file_format,
  })), [rows]);
  const [tableFilter, setTableFilter] = useState(tableData);
  const [sortingType, setSortingType] = useState("desc");
  const [sortColumn, setSortColumn] = useState("template_id");

  useEffect(() => {
    setTableFilter(tableData);
  }, [tableData]);

  const handleSorting = (column, sortType, type) => {
    setSortColumn(column);
    setTableFilter(sortData({ data: tableData, column, type, sortType }));
    setSortingType(sortingType === "asc" ? "desc" : "asc");
  };

  const sortableHeader = (label, column, type) => (
    <div
      onClick={() => handleSorting(column, sortingType === "asc" ? "desc" : "asc", type)}
      className="flex cursor-pointer justify-between"
    >
      <div>{label}</div>
      <div className="space-y-[-10px]">
        <FaSortUp className={sortColumn === column && sortingType === "asc" ? "opacity-100" : "opacity-50"} />
        <FaSortDown className={sortColumn === column && sortingType === "desc" ? "opacity-100" : "opacity-50"} />
      </div>
    </div>
  );

  const columns = React.useMemo(() => [
    {
      accessorKey: "sl",
      header: () => <div className="text-center">SR. NO.</div>,
      cell: (info) => <div className="text-center">{info.row.index + 1}.</div>,
    },
    {
      accessorKey: "select",
      header: ({ table }) => {
        const rowsToSelect = table.getCoreRowModel().rows.filter((row) => row.original.template_id);
        const allSelected = rowsToSelect.length > 0 && rowsToSelect.every((row) => row.getIsSelected());
        const someSelected = rowsToSelect.some((row) => row.getIsSelected());
        return (
          <div className="ml-[15px] flex w-full items-center justify-center">
            <IndeterminateCheckbox
              checked={allSelected}
              indeterminate={someSelected && !allSelected}
              onChange={() => rowsToSelect.forEach((row) => row.toggleSelected(!allSelected))}
            />
          </div>
        );
      },
      cell: ({ row }) => (
        <div className="ml-[15px] flex w-full items-center justify-center">
          <IndeterminateCheckbox checked={row.getIsSelected()} onChange={row.getToggleSelectedHandler()} />
        </div>
      ),
    },
    {
      accessorKey: "template_id",
      header: () => sortableHeader("Template ID", "template_id", "number"),
      cell: (info) => <div>{info.getValue()}</div>,
    },
    {
      accessorKey: "template_name",
      header: () => sortableHeader("Template Name", "template_name", "string"),
      cell: (info) => <div>{info.getValue()}</div>,
    },
    {
      accessorKey: "scheme_name",
      header: () => sortableHeader("PMS Scheme", "scheme_name", "string"),
      cell: (info) => <div>{info.getValue()}</div>,
    },
    {
      accessorKey: "file_format",
      header: () => sortableHeader("File Format", "file_format", "string"),
      cell: (info) => <div className="uppercase">{info.getValue()}</div>,
    },
    {
      accessorKey: "status",
      header: () => <div className="text-center">STATUS</div>,
      cell: (info) => <div className="text-center">{info.getValue() ? "Active" : "Inactive"}</div>,
    },
    {
      accessorKey: "action",
      header: () => <div className="text-center">Action</div>,
      cell: () => <div className="text-center" />,
    },
  ], [sortColumn, sortingType, tableData]);

  const actionConfig = [
    {
      id: "create-template",
      name: "Create Template",
      image: contractAddIcon,
      actionHandler: () => setPopup({ type: "template" }),
      disable: true,
    },
    {
      id: "map-template",
      name: "Map New Template To PMS",
      image: contractAddIcon,
      actionHandler: () => setPopup({ type: "mapping" }),
      disable: Boolean(templates.length && schemes.length),
    },
  ];

  return (
    <Table
      columns={columns}
      tableData={tableFilter}
      loading={loading}
      actionConfig={actionConfig}
      buttonConfig={{ handleSyncData }}
      syncClicked={syncing ? [syncing] : []}
      getSelectionValue={(row) => row.template_id}
      renderActions={(row) => (
        <div className="flex items-center justify-around gap-3">
          <button type="button" title="Edit mapping" onClick={() => setPopup({ type: "mapping", data: row.mapping })}>
            <FaPen />
          </button>
          <button type="button" title="Update template" onClick={() => setPopup({ type: "template", data: row.template })}>
            <RiImageEditFill className="text-lg" />
          </button>
        </div>
      )}
    />
  );
};

export default TemplatePMSConfig;
